import React, { ChangeEvent, useEffect, useState } from 'react';
import { Button, Col, Input, Modal, Row, Space, Tooltip, DatePicker, Tabs, Form } from 'antd';
import moment from 'moment';
import { RightOutlined, SendOutlined, LeftOutlined, EditOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import noop from 'lodash.noop';
import { Link } from 'react-router-dom';
import PageLayout from '../../SharedComponents/PageLayout';
import RSTable from '../../SharedComponents/RSTable';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { showUploadOperationCSVModal } from '../../store/app-ui.store';
import {
  fetchOperationSummary,
  getOperationSummary,
  getOperationSummaryLoading,
  LocationSummary,
  RouteSummary,
  sendSMS,
  updateRouteName,
} from '../../store/trip-actions.store';
import { getDraftOperations } from '../../store/operations.store';
import { fetchRoutes, fetchRouteDetails, getRoutes, getRoutesLoading } from '../../store/trips.store';
import { DEFAULT_PAGE_SIZE } from '../../Utils/constants';
import { useFeature } from '../../Utils/features';
import AnalyticsCard from '../../SharedComponents/AnalyticsCard';
import { getMinimalDrivers, getMinimalVehicles, fetchMinimalLicenseAndVehicle } from '../../store/drivers.store';
import MinimalDrivers from '../../SharedComponents/Forms/MinimalDrivers';
import MinimalVehicles from '../../SharedComponents/Forms/MinimalVehicles';

const { RangePicker } = DatePicker;

const renderDateTime = (value: string | null) => {
  if (!value || !moment(value).isValid()) return 'N/A';
  const m = moment(value);
  return (
    <div>
      <div>{m.format('YYYY-MM-DD')}</div>
      <div>{m.format('HH:mm:ss')}</div>
    </div>
  );
};

const getColumns = (
  onSendSms: (tripId: string) => Promise<any>,
  changeOperationInfo: (
    name: string,
    licenseNo: string,
    plateNumber: string,
    tripId: string,
    departureDateTime: string | null,
    arrivalDateTime: string | null,
  ) => Promise<any>,
  minimalDrivers: any[],
  minimalVehicles: any[],
) => [
  {
    title: 'Delivery ID',
    dataIndex: 'trip_id',
    rowKey: 'id',
  },
  { title: 'Route Name', dataIndex: 'trip_name', rowKey: 'trip_name' },
  {
    title: 'Start Date & Time',
    dataIndex: 'date_time',
    render: (text: string) => renderDateTime(text),
    rowKey: 'date_time',
  },
  {
    title: 'Vehicle Id',
    dataIndex: 'vehicle_id',
    rowKey: 'vehicle_id',
  },
  {
    title: 'Locations',
    dataIndex: 'number_of_locations',
    rowKey: 'location',
  },
  {
    title: 'Driver Name (Licence No)',
    dataIndex: 'license_no',
    rowKey: 'license_no',
    render: (_: any, record: any) => {
      const fullName = `${record.driver_fname ?? ''} ${record.driver_lname ?? ''}`.trim();
      return fullName ? `${fullName} (${record.license_no})` : record.license_no ?? 'N/A';
    },
  },
  {
    title: 'Status',
    dataIndex: 'trip_status',
    rowKey: 'tripStatus',
  },
  {
    title: 'Delivery Start',
    dataIndex: 'delivery_start',
    render: (text: string) => renderDateTime(text),
    rowKey: 'deliveryStart',
  },
  {
    title: 'Delivery Complete',
    dataIndex: 'delivery_end',
    render: (text: string) => renderDateTime(text),
    rowKey: 'deliveryEnd',
  },
  {
    title: '',
    rowKey: 'actions',
    render: (data: any, record: any) => (
      <Space size="middle">
        <Tooltip title="Send Routes to the driver as SMS" placement="bottomRight">
          <Button
            icon={<SendOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              Modal.confirm({
                title: 'Send route SMS to the driver?',
                icon: <SendOutlined />,
                okText: 'Send',
                onOk: async () => {
                  await onSendSms(record.trip_id);
                },
              });
            }}
          />
        </Tooltip>
        <Tooltip title="Update Name for the Delivery" placement="bottomRight">
          <Button
            icon={<EditOutlined />}
            onClick={(e) => {
              let formRef: any = null;
              e.stopPropagation();
              const matchedDriver = minimalDrivers.find((d) => d.licenseNumber === record.license_no);
              const driverValue = matchedDriver
                ? `${matchedDriver.fname} ${matchedDriver.lname} (${matchedDriver.licenseNumber})`
                : null;
              Modal.confirm({
                title: 'Enter a name for the selected route',
                icon: <EditOutlined />,
                okText: 'Save',
                onCancel: noop,
                width: 475,
                wrapClassName: 'route-edit-modal',
                content: (
                  <div className="route-edit-form">
                    <Form
                      labelCol={{ span: 7 }}
                      wrapperCol={{ span: 17 }}
                      ref={(r) => {
                        formRef = r;
                      }}
                      initialValues={{
                        name: record.trip_name,
                        driver: driverValue,
                        vehicle: record.vehicle_id,
                        departureDateTime: record.departure ? moment.utc(record.departure).local() : null,
                        arrivalDateTime: record.arrival ? moment.utc(record.arrival).local() : null,
                      }}
                    >
                      <Form.Item label="Name" name="name">
                        <Input />
                      </Form.Item>
                      <Form.Item label="Driver" name="driver" style={{ marginBottom: 12 }}>
                        <MinimalDrivers minimalDrivers={minimalDrivers} style={{ width: '100%', maxWidth: '100%' }} />
                      </Form.Item>
                      <Form.Item label="Vehicle" name="vehicle">
                        <MinimalVehicles minimalVehicle={minimalVehicles} />
                      </Form.Item>
                      <Form.Item
                        label={
                          <span>
                            Departure
                            <Tooltip title="Planned departure date time" placement="right">
                              <ExclamationCircleOutlined
                                className="ml-12 fs-14 c-orange"
                                style={{ marginLeft: '2px' }}
                              />
                            </Tooltip>
                          </span>
                        }
                        name="departureDateTime"
                      >
                        <DatePicker
                          showTime
                          format="YYYY-MM-DD HH:mm:ss"
                          style={{ width: '100%' }}
                          placeholder="Select date & time"
                        />
                      </Form.Item>
                      <Form.Item
                        label={
                          <span>
                            Arrival
                            <Tooltip title="Estimated arrival date time" placement="right">
                              <ExclamationCircleOutlined
                                className="ml-12 fs-14 c-orange"
                                style={{ marginLeft: '2px' }}
                              />
                            </Tooltip>
                          </span>
                        }
                        name="arrivalDateTime"
                      >
                        <DatePicker
                          showTime
                          format="YYYY-MM-DD HH:mm:ss"
                          style={{ width: '100%' }}
                          placeholder="Select date & time"
                        />
                      </Form.Item>
                    </Form>
                  </div>
                ),
                onOk: async () => {
                  const { name, driver, vehicle, departureDateTime, arrivalDateTime } = formRef.getFieldsValue();
                  const formattedDepartureDateTime = departureDateTime
                    ? `${moment(departureDateTime).utc().format('YYYY-MM-DDTHH:mm:ss')}Z`
                    : null;
                  const formattedArrivalDateTime = arrivalDateTime
                    ? `${moment(arrivalDateTime).utc().format('YYYY-MM-DDTHH:mm:ss')}Z`
                    : null;
                  await changeOperationInfo(
                    name,
                    driver,
                    vehicle,
                    record.trip_id,
                    formattedDepartureDateTime,
                    formattedArrivalDateTime,
                  );
                },
              });
            }}
          />
        </Tooltip>
      </Space>
    ),
  },
];

const Operations = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const routes = useAppSelector(getRoutes);
  const loading = useAppSelector(getRoutesLoading);

  const [filteredRoutes, setRoutes] = useState(routes);
  const [pageStart, setPageStart] = useState(0);
  const [filterLicense, setFilterLicense] = useState('');
  const [filterStart, setFilterStart] = useState('');
  const [filterEnd, setFilterEnd] = useState('');
  const [filterName, setFilterName] = useState('');

  const [showGoToEditing, setGoToEditing] = useState(false);
  const draftOperations = useAppSelector(getDraftOperations);

  const operationSummary = useAppSelector(getOperationSummary);
  const operationSummaryLoading = useAppSelector(getOperationSummaryLoading);

  const minimalDrivers = useAppSelector(getMinimalDrivers);
  const minimalVehicles = useAppSelector(getMinimalVehicles);

  const addOperation = useFeature('add_operation');

  const sendSms = (tripId: string) => dispatch(sendSMS({ tripId })).unwrap();

  useEffect(() => {
    if (minimalDrivers.length === 0 || minimalVehicles.length === 0) {
      dispatch(fetchMinimalLicenseAndVehicle(null));
    }
  }, []);

  const fetchSummaryAndRoutes = () => {
    dispatch(
      fetchOperationSummary({
        licenseNo: filterLicense,
        startDate: filterStart,
        endDate: filterEnd,
        tripName: filterName,
      }),
    );
    dispatch(
      fetchRoutes({
        start: pageStart,
        limit: DEFAULT_PAGE_SIZE,
        licenseNo: filterLicense,
        startDate: filterStart,
        endDate: filterEnd,
        tripName: filterName,
      }),
    );
  };

  useEffect(() => {
    if (draftOperations.length === 0) {
      const localDraftOperations = JSON.parse(localStorage.getItem('draft-operations') || '[]');
      if (localDraftOperations.length !== 0) {
        setGoToEditing(true);
      }
    } else {
      setGoToEditing(true);
    }
  }, []);

  useEffect(() => {
    setRoutes(routes);
  }, [routes]);

  useEffect(() => {
    fetchSummaryAndRoutes();
  }, [pageStart, filterLicense, filterStart, filterEnd, filterName]);

  const handleUploadCSV = () => {
    dispatch(showUploadOperationCSVModal(true));
  };

  const filter = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.trim()) {
      setRoutes(routes.filter((r) => String(r.trip_id).startsWith(e.target.value)));
    } else {
      setRoutes(routes);
    }
  };

  const updateOperationInfo = async (
    name: string,
    driverValue: string,
    vehicleValue: string,
    tripId: string,
    departureDateTime?: string | null,
    arrivalDateTime?: string | null,
  ) => {
    const driver = minimalDrivers.find(
      (d) => d.licenseNumber === driverValue || `${d.fname} ${d.lname} (${d.licenseNumber})` === driverValue,
    );
    const vehicle = minimalVehicles.find((v) => v.plateNumber === vehicleValue);

    await dispatch(
      updateRouteName({
        name,
        tripId,
        driverId: driver?.driver_id,
        vehicleId: vehicle?.vehicle_id,
        departureDateTime: departureDateTime ?? undefined,
        arrivalDateTime: arrivalDateTime ?? undefined,
      }),
    ).unwrap();
    fetchSummaryAndRoutes();
  };

  const changePage = (increment: boolean) => {
    if (!increment) {
      setPageStart(pageStart > 0 ? pageStart - DEFAULT_PAGE_SIZE : 0);
    } else {
      setPageStart(pageStart + DEFAULT_PAGE_SIZE);
    }
  };

  const filterElements = (
    <>
      <Col span={5}>
        <Input.Search placeholder="Route Name" onSearch={setFilterName} allowClear />
      </Col>
      <Col span={5}>
        <Input.Search placeholder="Driver License" onSearch={setFilterLicense} allowClear />
      </Col>
      <Col span={8}>
        <RangePicker
          showTime
          onCalendarChange={(dates, dateStrings: string[], info) => {
            if (info.range === 'end') {
              setFilterStart(dateStrings[0]);
              setFilterEnd(dateStrings[1]);
            }
          }}
        />
      </Col>
    </>
  );

  function isDefined<T>(value: T | null | undefined): value is T {
    return value !== null && value !== undefined;
  }
  function getSummaryTileValue(type: 'locations', category: keyof LocationSummary): number | string;
  function getSummaryTileValue(type: 'routes', category: keyof RouteSummary): number | string;
  function getSummaryTileValue<T extends 'locations' | 'routes'>(
    type: T,
    category: T extends 'locations' ? keyof LocationSummary : keyof RouteSummary,
  ): number | string {
    if (!isDefined(operationSummary)) return 'N/A';
    if (type === 'locations') {
      const summary = operationSummary.locations as LocationSummary | undefined;
      if (!isDefined(summary)) return 'N/A';
      return summary[category as keyof LocationSummary] ?? 0;
    }
    if (type === 'routes') {
      const summary = operationSummary.routes as RouteSummary | undefined;
      if (!isDefined(summary)) return 'N/A';
      return summary[category as keyof RouteSummary] ?? 0;
    }
    return 'N/A';
  }

  const getSummaryLocationOutput = () => (
    <div className="d-flex jc-space-around w-100">
      <AnalyticsCard
        value={getSummaryTileValue('locations', 'cancelled')}
        description="Cancelled"
        isLoading={operationSummaryLoading}
      />
      <AnalyticsCard
        value={getSummaryTileValue('locations', 'delivered')}
        description="Delivered"
        isLoading={operationSummaryLoading}
      />
      <AnalyticsCard
        value={getSummaryTileValue('locations', 'planned')}
        description="Planned"
        isLoading={operationSummaryLoading}
      />
      <AnalyticsCard
        value={getSummaryTileValue('locations', 'skipped')}
        description="Skipped"
        isLoading={operationSummaryLoading}
      />
    </div>
  );

  const getSummaryRouteOutput = () => (
    <div className="d-flex jc-space-around w-100">
      <AnalyticsCard
        value={getSummaryTileValue('routes', 'completed')}
        description="Complete"
        isLoading={operationSummaryLoading}
      />
      <AnalyticsCard
        value={getSummaryTileValue('routes', 'in_progress')}
        description="In Progress"
        isLoading={operationSummaryLoading}
      />
      <AnalyticsCard
        value={getSummaryTileValue('routes', 'planned')}
        description="Planned"
        isLoading={operationSummaryLoading}
      />
      <AnalyticsCard
        value={getSummaryTileValue('routes', 'unplanned')}
        description="Unplanned"
        isLoading={operationSummaryLoading}
      />
    </div>
  );

  return (
    <PageLayout
      title="Trips"
      actions={
        addOperation
          ? [
              showGoToEditing ? (
                <Link to="operations/edit-operation">
                  <Button key="go-to-editing">Go To Editing</Button>
                </Link>
              ) : null,
              <Button type="primary" key="upload" onClick={handleUploadCSV}>
                Upload CSV
              </Button>,
            ]
          : []
      }
    >
      <Row>
        <Tabs tabPosition="left" className="w-100">
          <Tabs.TabPane key="routes" tab="Routes">
            {getSummaryRouteOutput()}
          </Tabs.TabPane>
          <Tabs.TabPane key="location" tab="Location">
            {getSummaryLocationOutput()}
          </Tabs.TabPane>
        </Tabs>
        <RSTable
          loading={loading}
          onSearch={filter}
          headerTitle="Trips Summary"
          columns={getColumns(sendSms, updateOperationInfo, minimalDrivers, minimalVehicles)}
          filterElements={filterElements}
          dataSource={filteredRoutes}
          onRow={(record) => ({
            onClick: () => {
              dispatch(fetchRouteDetails({ tripID: record.trip_id, licenseNo: record.license_no }));
            },
          })}
          footer={(pageData) => (
            <Row justify="end">
              <Space size="small">
                <Button icon={<LeftOutlined />} onClick={() => changePage(false)} />
                <Button
                  icon={<RightOutlined />}
                  onClick={() => changePage(true)}
                  disabled={pageData.length < DEFAULT_PAGE_SIZE}
                />
              </Space>
            </Row>
          )}
          rowKey="trip_id"
        />
      </Row>
    </PageLayout>
  );
};

export default Operations;
